import React from "react";
import {
  generatePath,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
  useResolvedPath,
} from "react-router-dom-v5-compat";

type DialogProps = {
  onClose: () => void;
};

export const useDialogRoute = <TParams,>(
  pathFragment: string,
  renderDialog: (props: DialogProps) => React.ReactNode,
) => {
  const navigate = useNavigate();
  const url = useResolvedPath("").pathname;
  const safeToGoBack = React.useRef(false);
  const location = useLocation();

  const [initialSearch, setInitialSearch] = React.useState(location.search);

  type Open = [keyof TParams] extends [never] ? () => void : (params: TParams) => void;

  const open: Open = (params?: TParams) => {
    const path = generatePath(`${url}/${pathFragment}`, params ?? {});
    navigate(path + location.search, {});
    safeToGoBack.current = true;
    setInitialSearch(location.search);
  };

  const close = () => {
    if (location.search !== initialSearch) {
      navigate(url + location.search, { replace: true });
      return;
    }

    if (safeToGoBack.current) {
      navigate(-1);
      safeToGoBack.current = false;
      return;
    }

    navigate(url + location.search, { replace: true });
  };

  const isOpen = useMatch(`${url}/${pathFragment}`)?.pathname === `${url}/${pathFragment}`;

  const element = (
    <Routes>
      <Route path={`${pathFragment}`} element={renderDialog({ onClose: close })} />
    </Routes>
  );
  return { open, close, element, isOpen };
};
